import React, { useState } from 'react';
import MainLayout from '../../components/Layout/MainLayout';
import {
  PageCard, BtnPrimary, BtnSecondary,
  FormField, FormInput, FormSelect, Alert
} from '../../components/UI/UIComponents';
import './JoinMeeting.css';

const CLIENT_URL = process.env.REACT_APP_CLIENT_URL || `http://localhost:${process.env.REACT_APP_CLIENT_PORT || 3006}`;

const LinkIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>;
const UserIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const GlobeIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>;

const LANGUAGES = [
  { value: 'en-US',  label: 'English',    flag: '🇺🇸', sub: 'I speak English',    native: 'English'    },
  { value: 'ja-JP',  label: 'Japanese',   flag: '🇯🇵', sub: '日本語で話します',     native: '日本語'       },
  { value: 'zh-CN',  label: 'Chinese',    flag: '🇨🇳', sub: '我说普通话',          native: '中文'         },
  { value: 'vi-VN',  label: 'Vietnamese', flag: '🇻🇳', sub: 'Tôi nói tiếng Việt', native: 'Tiếng Việt'  },
  { value: 'bn-BD',  label: 'Bangla',     flag: '🇧🇩', sub: 'আমি বাংলায় কথা বলি',  native: 'বাংলা'        },
]

const TIPS = [
  { icon: '🔗', text: 'Paste the Zoom meeting link shared by the host.' },
  { icon: '🎤', text: 'Allow microphone access when prompted.' },
  { icon: '🌐', text: 'Choose the language you will be speaking in.' },
  { icon: '🔊', text: 'Wear headphones to avoid audio echo issues.' },
];

const JoinMeeting = () => {
  const [meetingLink, setMeetingLink] = useState('');
  const [name,        setName]        = useState('');
  const [language,    setLanguage]    = useState('en-US');
  const [loading,     setLoading]     = useState(false);
  const [errors,      setErrors]      = useState({});
  const [error,       setError]       = useState('');

  const validate = () => {
    const e = {};
    if (!meetingLink.trim()) e.link = 'Please paste the Zoom meeting link.';
    else if (!meetingLink.includes('zoom.us')) e.link = 'This does not look like a valid Zoom meeting link.';
    if (!name.trim()) e.name = 'Please enter your display name.';
    if (!language)    e.language = 'Please select your language.';
    return e;
  };

  const handleJoin = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setError('');
    setLoading(true);
    try {
      // Fix: proper URL replacement instead of fragile split-by-? index
      const newUrl = meetingLink.trim().replace(
        /https?:\/\/[a-z0-9.]*zoom\.us\/j\//,
        `${CLIENT_URL}?`
      ) + `?role=0?name=${encodeURIComponent(name.trim())}?lang=${language}?room=development`;
      window.location.replace(newUrl);
    } catch (err) {
      setError('Invalid meeting link format. Please check and try again.');
      setLoading(false);
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setMeetingLink(text);
      setErrors(p => ({...p, link: ''}));
    } catch {
      setError('Unable to read clipboard. Please paste manually.');
    }
  };

  return (
    <MainLayout pageTitle="Join Meeting" pageSubtitle="Enter an existing AI-interpreted Zoom session">
      <div className="join-meeting">

        {/* Form */}
        <div className="jm-form-col">
          <PageCard title="Join a Meeting" subtitle="Paste the meeting link from your host">

            {error && <Alert type="error">{error}</Alert>}

            <FormField label="Meeting Link" error={errors.link} required>
              <div className="jm-link-wrap">
                <div className="form-input-wrap has-icon" style={{flex:1}}>
                  <span className="form-input-icon"><LinkIcon/></span>
                  <input
                    className="form-input"
                    type="url"
                    placeholder="https://us05web.zoom.us/j/..."
                    value={meetingLink}
                    onChange={e => { setMeetingLink(e.target.value); setErrors(p=>({...p,link:''})); }}
                  />
                </div>
                <button className="jm-paste-btn" onClick={handlePaste} title="Paste from clipboard">
                  📋 Paste
                </button>
              </div>
            </FormField>

            <FormField label="Your Display Name" error={errors.name} required>
              <FormInput
                icon={<UserIcon/>}
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={e => { setName(e.target.value); setErrors(p=>({...p,name:''})); }}
                maxLength={40}
              />
            </FormField>

            <FormField label="Your Speaking Language" error={errors.language} required>
              <div className="jm-lang-cards">
                {LANGUAGES.map(l => (
                  <button
                    key={l.value}
                    type="button"
                    className={`jm-lang-card${language === l.value ? ' jm-lang-card--active' : ''}`}
                    onClick={() => { setLanguage(l.value); setErrors(p=>({...p,language:''})); }}
                  >
                    <span className="jm-lang-flag">{l.flag}</span>
                    <span className="jm-lang-card-name">{l.native}</span>
                    <span className="jm-lang-card-sub">{l.sub}</span>
                  </button>
                ))}
              </div>
            </FormField>


            <div className="jm-actions">
              <BtnPrimary onClick={handleJoin} loading={loading} fullWidth>
                {loading ? '' : '🔗  Join Meeting'}
              </BtnPrimary>
              <BtnSecondary onClick={() => window.history.back()} fullWidth>
                Go Back
              </BtnSecondary>
            </div>
          </PageCard>
        </div>

        {/* Tips */}
        <div className="jm-tips-col">
          <PageCard title="Before you join" subtitle="Tips for the best experience">
            <div className="jm-tips-list">
              {TIPS.map((t, i) => (
                <div className="jm-tip" key={i}>
                  <span className="jm-tip-icon">{t.icon}</span>
                  <p className="jm-tip-text">{t.text}</p>
                </div>
              ))}
            </div>
          </PageCard>

          <div className="jm-info-card">
            <div className="jm-info-icon">🔐</div>
            <div>
              <p className="jm-info-title">Privacy Note</p>
              <p className="jm-info-desc">Your speech is processed in real-time and not permanently stored. Transcripts exist only for the duration of your session.</p>
            </div>
          </div>

          <div className="jm-info-card jm-info-card--blue">
            <div className="jm-info-icon">💡</div>
            <div>
              <p className="jm-info-title">No Account Needed</p>
              <p className="jm-info-desc">Participants don't need a Zoom account or any special software — just paste the meeting link and go.</p>
            </div>
          </div>
        </div>

      </div>
    </MainLayout>
  );
};

export default JoinMeeting;